import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, MapPin, Share2, Sparkles, Wallet } from 'lucide-react';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon,
} from 'react-share';

const counties = [
  'carlow',
  'cavan',
  'clare',
  'cork',
  'donegal',
  'dublin',
  'galway',
  'kerry',
  'kildare',
  'kilkenny',
  'laois',
  'leitrim',
  'limerick',
  'longford',
  'louth',
  'mayo',
  'meath',
  'monaghan',
  'offaly',
  'roscommon',
  'sligo',
  'tipperary',
  'waterford',
  'westmeath',
  'wexford',
  'wicklow',
];

const salaries = Array.from({ length: 16 }, (_, index) => 25000 + index * 5000);

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value || 0);

const toTitleCase = (value: string) =>
  value
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const toPath = (salary: number, county: string) => `/salary/is-${salary}-a-good-salary-in-${county}`;

const GoodSalaryDiscoverPage: React.FC = () => {
  const [selectedCounty, setSelectedCounty] = useState('dublin');
  const [selectedSalary, setSelectedSalary] = useState(60000);

  const featuredCards = useMemo(
    () => [
      { county: 'dublin', salary: 45000 },
      { county: 'dublin', salary: 60000 },
      { county: 'cork', salary: 50000 },
      { county: 'galway', salary: 55000 },
      { county: 'limerick', salary: 45000 },
      { county: 'kildare', salary: 65000 },
      { county: 'waterford', salary: 40000 },
      { county: 'leitrim', salary: 35000 },
      { county: 'meath', salary: 50000 },
      { county: 'wicklow', salary: 70000 },
      { county: 'wexford', salary: 40000 },
      { county: 'mayo', salary: 40000 },
      { county: 'donegal', salary: 35000 },
      { county: 'clare', salary: 45000 },
      { county: 'sligo', salary: 45000 },
      { county: 'louth', salary: 50000 },
    ],
    []
  );

  const canonicalUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/salary`
      : 'https://rentsmart.ie/salary';
  const shareUrl = typeof window !== 'undefined' ? window.location.href : canonicalUrl;
  const shareText =
    'RentSmart.ie helps you find properties to rent in Ireland first, then compare salary reality, rent pressure, and what you can afford by county.';

  return (
    <div className="relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-x-hidden bg-slate-50">
      <Helmet>
        <title>Is This a Good Salary in Ireland? County Salary Checker (2026)</title>
        <meta
          name="description"
          content="Check if your salary is good for any Irish county. See net pay, rent realism, disposable income and properties you can afford."
        />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <header className="bg-slate-900 text-white pt-14 pb-16 px-4 relative overflow-hidden">
        <div className="absolute -top-12 -right-8 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="absolute -bottom-20 -left-8 h-72 w-72 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-widest mb-5">
            <Sparkles className="h-4 w-4" /> RentSmart.ie Salary Intelligence
          </div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-4">
            Is Your Salary Good for the County You Want?
          </h1>
          <p className="text-slate-300 max-w-3xl text-lg leading-relaxed">
            Compare salary reality across Ireland with county-level rent pressure, take-home pay and
            lifestyle affordability. Built for relocators, graduates and professionals.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest text-slate-400">
              <Share2 className="h-3.5 w-3.5" /> Share
            </span>
            <FacebookShareButton url={shareUrl} hashtag="#RentSmartIE">
              <FacebookIcon size={30} round />
            </FacebookShareButton>
            <TwitterShareButton url={shareUrl} title={shareText}>
              <TwitterIcon size={30} round />
            </TwitterShareButton>
            <WhatsappShareButton url={shareUrl} title={shareText}>
              <WhatsappIcon size={30} round />
            </WhatsappShareButton>
            <LinkedinShareButton
              url={shareUrl}
              title="Good Salary Checker | RentSmart.ie"
              summary={shareText}
              source="RentSmart.ie"
            >
              <LinkedinIcon size={30} round />
            </LinkedinShareButton>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 -mt-10 pb-16">
        <section className="mt-14 rounded-[2rem] border border-slate-100 bg-white p-6 md:p-8 shadow-xl shadow-slate-200/40 mb-8">
          <h2 className="text-2xl font-black text-slate-900 mb-5">What Does Your Salary Really Get You?</h2>
          <div className="grid md:grid-cols-12 gap-4">
            <div className="md:col-span-4">
              <label className="text-xs font-black uppercase tracking-wider text-slate-500">
                Gross Salary
              </label>
              <select
                value={selectedSalary}
                onChange={(event) => setSelectedSalary(Number(event.target.value))}
                className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 font-black text-slate-900 bg-white"
              >
                {salaries.map((salary) => (
                  <option key={salary} value={salary}>
                    {formatCurrency(salary)}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-4">
              <label className="text-xs font-black uppercase tracking-wider text-slate-500">
                County
              </label>
              <select
                value={selectedCounty}
                onChange={(event) => setSelectedCounty(event.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 font-bold text-slate-800 bg-white"
              >
                {counties.map((county) => (
                  <option key={county} value={county}>
                    {toTitleCase(county)}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-4 flex items-end">
              <Link
                to={toPath(selectedSalary, selectedCounty)}
                className="no-underline inline-flex w-full items-center justify-between rounded-xl bg-emerald-600 px-4 py-3 text-white font-black hover:bg-emerald-700 transition-colors"
              >
                Show me <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
          <p className="text-sm text-slate-500 mt-3">
            Straight talk for Ireland: {formatCurrency(selectedSalary)} in {toTitleCase(selectedCounty)}.
          </p>
        </section>

        <section className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-wider text-slate-500 mb-2">
              <Wallet className="h-4 w-4 text-emerald-600" /> Net Pay Lens
            </div>
            <p className="text-sm font-semibold text-slate-700">
              Full PAYE, USC and PRSI breakdown for each salary band.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-wider text-slate-500 mb-2">
              <MapPin className="h-4 w-4 text-sky-600" /> County Fit Lens
            </div>
            <p className="text-sm font-semibold text-slate-700">
              Rent realism and disposable income after essentials by county.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-wider text-slate-500 mb-2">
              <Building2 className="h-4 w-4 text-violet-600" /> Property CTA
            </div>
            <p className="text-sm font-semibold text-slate-700">
              Jump from salary verdict to live rental and sales listings instantly.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-black text-slate-900 mb-4">Popular Good Salary Checks</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {featuredCards.map((item) => (
              <Link
                key={`${item.salary}-${item.county}`}
                to={toPath(item.salary, item.county)}
                className="no-underline rounded-2xl border border-slate-200 bg-white p-5 hover:-translate-y-1 hover:shadow-lg transition-all"
              >
                <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">
                  Salary Verdict
                </p>
                <p className="text-xl font-black text-slate-900 mb-1">{formatCurrency(item.salary)}</p>
                <p className="text-sm font-semibold text-slate-600 mb-4">in {toTitleCase(item.county)}</p>
                <p className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-wider text-emerald-700">
                  Open report <ArrowRight className="h-3 w-3" />
                </p>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-8 rounded-2xl border border-slate-200 bg-slate-100 p-5">
          <p className="text-xs text-slate-600 font-semibold leading-relaxed">
            Estimates are informational only and do not constitute financial, tax, mortgage, legal
            or investment advice. Always confirm figures with qualified professionals.
          </p>
        </section>
      </main>
    </div>
  );
};

export default GoodSalaryDiscoverPage;
